import { useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

export const useUnlockCode = () => {
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  const validateAndRedeemCode = async (code: string): Promise<{ success: boolean; error?: string }> => {
    if (!code || code.trim().length === 0) {
      return { success: false, error: 'Please enter a code' }
    }

    setLoading(true)
    
    try {
      const normalizedCode = code.trim().toUpperCase()
      
      // 1. Get current user
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        return { success: false, error: 'You must be logged in to redeem a code' }
      }
      
      // 2. Verify code exists in database
      const { data: codeData, error } = await supabase
        .from('payment_codes')
        .select('*')
        .eq('code', normalizedCode)
        .single()
      
      if (error || !codeData) {
        return { success: false, error: 'Invalid code' }
      }
      
      // 3. Check if already used
      if (codeData.is_used && codeData.used_by_user_id) {
        if (codeData.used_by_user_id !== user.id) {
          return { success: false, error: 'Code already used by another user' }
        }
        
        // If used by same user, check if they already entered it
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('has_entered_unlock_code')
          .eq('id', user.id)
          .single()
        
        if (existingProfile?.has_entered_unlock_code) {
          // Already entered, just return success
          return { success: true }
        }
        // Code was used but flag not set - continue to update it
      }
      
      // 4. Get user's profile and verify code matches their assigned code
      const { data: profile } = await supabase
        .from('profiles')
        .select('payment_status, assigned_unlock_code')
        .eq('id', user.id)
        .single()
      
      if (!profile) {
        return { success: false, error: 'Profile not found' }
      }
      
      // CRITICAL: Verify admin has confirmed payment
      if (profile.payment_status !== 'paid') {
        return { success: false, error: 'Admin has not confirmed your payment yet. Please contact support or wait for payment confirmation.' }
      }
      
      // CRITICAL: Verify this code belongs to this user
      if (profile.assigned_unlock_code !== normalizedCode) {
        return { success: false, error: 'This code is not assigned to your account. Please use your assigned code.' }
      }
      
      // 5. Update profile FIRST (this is critical - must happen before marking code as used)
      console.log('🔐 Updating profile has_entered_unlock_code for user:', user.id)
      const { error: profileError, data: profileUpdateData } = await supabase
        .from('profiles')
        .update({
          has_entered_unlock_code: true
        })
        .eq('id', user.id)
        .select()
      
      if (profileError) {
        console.error('❌ Error updating profile:', profileError)
        console.error('❌ Error details:', JSON.stringify(profileError, null, 2))
        toast({
          title: "Error",
          description: "Failed to update profile. Please contact support.",
          variant: "destructive"
        })
        return { success: false, error: 'Failed to update profile' }
      }
      
      console.log('✅ Successfully updated profile:', profileUpdateData)
      
      // 6. Now mark code as used (optional - don't fail if this errors)
      const { error: updateError } = await supabase
        .from('payment_codes')
        .update({
          is_used: true,
          used_by_user_id: user.id,
          used_at: new Date().toISOString()
        })
        .eq('code', normalizedCode)
      
      if (updateError) {
        console.warn('⚠️ Could not mark code as used in payment_codes table:', updateError)
        // Don't fail - profile is already updated
      }
      
      toast({
        title: "Account verified! 🎉",
        description: "Your account has been unlocked. Welcome to Dably!",
      })
      
      return { success: true }
    } catch (error) {
      console.error('Unexpected error during code validation:', error)
      return { success: false, error: 'An unexpected error occurred. Please try again.' }
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    validateAndRedeemCode
  }
}
